import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/** Maps the first URL segment / Home card id to the permission module code. */
const PATH_MODULE: Record<string, string> = {
  dashboard: 'dashboard',
  checklist: 'inspections',
  inspections: 'inspections',
  sectors: 'inspections',
  workforce: 'workforce',
  trainings: 'trainings',
  traceability: 'traceability',
  equipamentos: 'equipment',
  projetos: 'projects',
  cronograma: 'schedule',
  planejamento: 'planning',
  estoque: 'stock',
  compras: 'purchases',
  diario: 'site_diary',
  sac: 'sac',
  'canais-ia': 'chatbots',
  feedback: 'feedback',
  works: 'works',
};

export function moduleCodeForPath(pathOrId: string): string | null {
  const seg = pathOrId.replace(/^\//, '').split('/')[0];
  return PATH_MODULE[seg] ?? null;
}

/** Card ids on Home that differ from their path segment. */
export function moduleCodeForCard(id: string, path: string): string | null {
  return PATH_MODULE[id] ?? moduleCodeForPath(path);
}

/**
 * Returns the set of module codes the user may view (granted "view" action).
 * Admins / Super Admins bypass (null = unrestricted).
 */
export function useModuleAccess() {
  const { profile, isAdmin } = useAuth();
  const q = useQuery({
    queryKey: ['module-view-access', profile?.id],
    enabled: !!profile?.id && !isAdmin,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_permissions')
        .select('granted, permission_actions!inner(code, permission_modules!inner(code))')
        .eq('user_id', profile!.id)
        .eq('granted', true);
      if (error) throw error;
      const set = new Set<string>();
      (data || []).forEach((r: any) => {
        const a = r.permission_actions;
        if (a?.code === 'view' && a.permission_modules?.code) set.add(a.permission_modules.code);
      });
      return set;
    },
  });

  const unrestricted = isAdmin;
  const canView = (code: string | null) => {
    if (unrestricted || !code) return true;
    return q.data?.has(code) ?? false;
  };
  return { canView, loading: !unrestricted && !!profile?.id && q.isLoading };
}

/**
 * Granular action check from Cadastro → Permissões.
 * Admins / Super Admins are unrestricted; everyone else needs the action granted.
 */
export function useCan() {
  const { profile, isAdmin } = useAuth();
  const q = useQuery({
    queryKey: ['user-granted-actions', profile?.id],
    enabled: !!profile?.id && !isAdmin,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_permissions')
        .select('permission_actions!inner(code, permission_modules!inner(code))')
        .eq('user_id', profile!.id)
        .eq('granted', true);
      if (error) throw error;
      const set = new Set<string>();
      (data || []).forEach((r: any) => {
        const a = r.permission_actions;
        if (a?.code && a.permission_modules?.code) set.add(`${a.permission_modules.code}.${a.code}`);
      });
      return set;
    },
  });
  return (module: string, action: string) => isAdmin || (q.data?.has(`${module}.${action}`) ?? false);
}
